import { useState, useEffect, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Localization from "aldoo-ra/Localization"
import { PermissionAPI } from "aldoo-ra/Permissions"
import { GetParam } from "aldoo-ra/Admin/ParamIO/api"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import LiveEditRegistry from "./registry"
import { liveEditSave } from "./api"

const PARAM = "LOCALIZATION_LIVE_EDIT"
const EMPTY_LABEL = "[empty]"

// True if any ancestor is position: fixed | sticky (so the text doesn't scroll).
function hasFixedAncestor(el) {
  let node = el
  while (node && node !== document.body) {
    const pos = getComputedStyle(node).position
    if (pos === "fixed" || pos === "sticky") return true
    node = node.parentElement
  }
  return false
}

// Walk visible leaf text nodes and match each (whole, trimmed) against the
// registry. Leaf-only + exact-match means no nested rects and no substring
// collisions ("Test" never matches inside "Test here").
function scanTargets() {
  if (typeof document === "undefined") return []
  const targets = []
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const text = node.nodeValue
      if (!text || !text.trim()) return NodeFilter.FILTER_REJECT
      const el = node.parentElement
      if (!el) return NodeFilter.FILTER_REJECT
      if (el.closest("[data-live-edit-ui]")) return NodeFilter.FILTER_REJECT
      const tag = el.tagName
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return NodeFilter.FILTER_REJECT
      const sources = LiveEditRegistry.lookup(text)
      return sources.length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    },
  })
  let n
  while ((n = walker.nextNode())) {
    const range = document.createRange()
    range.selectNodeContents(n)
    const rect = range.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) continue
    // Inherit the element's positioning context: if a fixed/sticky ancestor
    // exists, pin the overlay to the viewport so it doesn't scroll away.
    const fixed = hasFixedAncestor(n.parentElement)
    const sx = window.scrollX || window.pageXOffset
    const sy = window.scrollY || window.pageYOffset
    targets.push({
      node: n,
      text: n.nodeValue.trim(),
      sources: LiveEditRegistry.lookup(n.nodeValue),
      fixed,
      // viewport coords; absolute overlays add scroll offset at render
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      scroll: { x: sx, y: sy },
    })
  }
  return targets
}

export default function LiveEdit() {
  const [auth] = PlatformAuth.useAuth()
  const { currentLanguage } = Localization()
  const [available, setAvailable] = useState(false)
  const [active, setActive] = useState(false)
  const [targets, setTargets] = useState([])
  const [edit, setEdit] = useState(null) // { target, source, value }
  const [saving, setSaving] = useState(false)
  const rescanRef = useRef(null)
  const editingRef = useRef(false)
  useEffect(() => { editingRef.current = !!edit }, [edit])
  // Nodes whose value was emptied this session — show "[empty]" only while active.
  const emptyNodesRef = useRef([])
  const showPlaceholders = () => emptyNodesRef.current.forEach(({ node }) => {
    if (node.nodeValue.trim() === "") node.nodeValue = EMPTY_LABEL
  })
  const hidePlaceholders = () => emptyNodesRef.current.forEach(({ node }) => {
    if (node.nodeValue.trim() === EMPTY_LABEL) node.nodeValue = ""
  })

  // Gate: ParamIO flag + role:admin.
  useEffect(() => {
    let alive = true
    if (!auth?._id) { setAvailable(false); return }
    Promise.all([
      GetParam(PARAM).then((r) => r?.value === true || r?.value === "true").catch(() => false),
      PermissionAPI.hasPermission(auth._id, "role:admin").catch(() => false),
    ]).then(([flag, admin]) => { if (alive) setAvailable(!!flag && !!admin) })
    return () => { alive = false }
  }, [auth])

  const rescan = useCallback(() => setTargets(scanTargets()), [])

  // Alt+E toggles overlay.
  useEffect(() => {
    if (!available) return
    const onKey = (e) => {
      // Use e.code — on macOS Alt+E is a dead key, so e.key isn't "e".
      if (e.altKey && e.code === "KeyE") {
        e.preventDefault()
        setActive((v) => !v)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [available])

  // While active, keep overlays positioned.
  useEffect(() => {
    if (!active) { hidePlaceholders(); setTargets([]); return }
    showPlaceholders()
    rescan()
    const onChange = () => {
      if (editingRef.current) return // freeze overlays while the editor is open
      clearTimeout(rescanRef.current)
      rescanRef.current = setTimeout(rescan, 120)
    }
    // Ignore mutations originating from our own overlay UI to avoid a feedback loop.
    const onMutate = (mutations) => {
      if (editingRef.current) return
      const fromSelf = mutations.every((m) => {
        const node = m.target.nodeType === 1 ? m.target : m.target.parentElement
        return node && node.closest && node.closest("[data-live-edit-ui]")
      })
      if (fromSelf) return
      onChange()
    }
    window.addEventListener("resize", onChange)
    window.addEventListener("scroll", onChange, true)
    const mo = new MutationObserver(onMutate)
    mo.observe(document.body, { childList: true, subtree: true, characterData: true })
    return () => {
      window.removeEventListener("resize", onChange)
      window.removeEventListener("scroll", onChange, true)
      mo.disconnect()
      clearTimeout(rescanRef.current)
      hidePlaceholders()
    }
  }, [active, rescan])

  const openEditor = (target) => {
    const source = target.sources[0]
    // Placeholder nodes edit from blank, not the literal "[empty]".
    setEdit({ target, source, value: source?.empty ? "" : target.text })
  }

  const apply = async () => {
    if (!edit) return
    const { target, source, value } = edit
    setSaving(true)
    try {
      const res = await liveEditSave({
        key: source.localizationKey,
        bundleKey: source.bundleKey,
        contentId: source.contentId,
        field: source.field,
        language: currentLanguage,
        value,
      })
      if (res?.error) throw new Error(res.error)
      // Optimistically update the live DOM + registry. Empty values would render
      // nothing (no anchor), so show an [empty] placeholder that stays clickable.
      const isEmpty = value.trim() === ""
      // Placeholder shows only while live-edit is on; the real value is empty.
      const display = isEmpty ? EMPTY_LABEL : value
      try { target.node.nodeValue = target.node.nodeValue.replace(target.text, display) } catch (_) {}
      LiveEditRegistry.remap(target.text, display, { ...source, empty: isEmpty })
      if (isEmpty) {
        if (!emptyNodesRef.current.some((e) => e.node === target.node)) {
          emptyNodesRef.current.push({ node: target.node, source })
        }
      } else {
        emptyNodesRef.current = emptyNodesRef.current.filter((e) => e.node !== target.node)
      }
      setEdit(null)
      rescan()
    } catch (_) {
      // keep dialog open on failure
    } finally {
      setSaving(false)
    }
  }

  if (!available) return null

  return (
    <div data-live-edit-ui>
      {/* Mode indicator */}
      {active && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[9998] bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg pointer-events-none">
          Live Edit — click a text to edit ({currentLanguage}). Alt+E to exit.
        </div>
      )}

      {/* Overlays — absolute (page coords) so they scroll with content */}
      {active && createPortal(
        <div data-live-edit-ui>
          {targets.map((tgt, i) => (
            <div
              key={i}
              onClick={() => openEditor(tgt)}
              title="Edit text"
              className={`z-[9997] rounded-sm cursor-pointer transition-colors ring-1 ${tgt.fixed ? "fixed" : "absolute"} ${
                tgt.sources?.[0]?.empty
                  ? "bg-amber-400/30 hover:bg-amber-400/50 ring-amber-500/70"
                  : "bg-blue-500/25 hover:bg-blue-500/40 ring-blue-500/60"
              }`}
              style={{
                top: `${tgt.rect.top - 2 + (tgt.fixed ? 0 : tgt.scroll.y)}px`,
                left: `${tgt.rect.left - 2 + (tgt.fixed ? 0 : tgt.scroll.x)}px`,
                width: `${tgt.rect.width + 4}px`,
                height: `${tgt.rect.height + 4}px`,
              }}
            />
          ))}
        </div>,
        document.body
      )}

      {/* Editor dialog */}
      {edit && createPortal(
        <Modal isOpen={!!edit} onClose={() => setEdit(null)} title="Edit text" variant="compact" className="max-w-lg" closeOnClickOutside closeOnEscape zIndex={9999}>
          <div className="p-5 flex flex-col gap-4">
            {edit.target.sources.length > 1 && (
              <select
                value={edit.target.sources.indexOf(edit.source)}
                onChange={(e) => setEdit({ ...edit, source: edit.target.sources[Number(e.target.value)] })}
                className="w-full rounded-xl border border-bordered bg-canvas px-3 py-2 text-sm text-text"
              >
                {edit.target.sources.map((s, i) => (
                  <option key={i} value={i}>
                    {s.bundleKey ? `${s.field}.${s.bundleKey}` : s.field} · {s.localizationKey || `${s.contentId}`}
                  </option>
                ))}
              </select>
            )}
            <textarea
              autoFocus
              value={edit.value}
              onChange={(e) => setEdit({ ...edit, value: e.target.value })}
              rows={5}
              className="w-full rounded-xl border border-bordered bg-transparent px-3 py-2.5 text-sm text-text focus:outline-none focus:border-primary resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
              <Button variant="primary" onClick={apply} disabled={saving}>
                {saving ? <Spinner variant="minimal" /> : "Apply"}
              </Button>
            </div>
          </div>
        </Modal>,
        document.body
      )}
    </div>
  )
}
